import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import { validateNonNegative } from '../../../utils/validation';
import IngredientPicker from '../../modals/IngredientPicker';
import ingredientApi from '../../../services/ingredientApi';
import './ProductEditPanel.css';

/**
 * Product Edit Panel Component
 * Provides an inline form for editing product details
 */
const ProductEditPanel = ({
                              product,
                              onSave,
                              onCancel,
                              isSaving = false,
                              validationErrors = {}
                          }) => {
    // We're not using these yet, but keeping them for future use
    // eslint-disable-next-line no-unused-vars
    const { showSuccess, showError } = useToast();
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [selectedIngredients, setSelectedIngredients] = useState([]);

    // Initialize form with product data
    useEffect(() => {
        if (product) {
            setFormData({
                flavor: product.flavor || '',
                coffee_cost: product.coffee_cost || 0,
                ingredient_cost: product.ingredient_cost || 0,
                can_cost: product.can_cost || 0,
                packing_cost: product.packing_cost || 0,
                total_cogs: product.total_cogs || 0,
                wholesale_price: product.wholesale_price || 0,
                retail_price: product.retail_price || 0,
                total_wholesale_volume: product.total_wholesale_volume || 0,
                coffee_spend: product.coffee_spend || 0,
                ingredient_spend: product.ingredient_spend || 0,
                is_active: product.is_active !== false, // default to true if undefined
                description: product.description || '',
                notes: product.notes || ''
            });

            // Populate ingredients
            if (product.ingredients && product.ingredients.length > 0) {
                const fetchIngredientDetails = async () => {
                    try {
                        // Fetch all available ingredients to cross-reference names, etc.
                        const response = await ingredientApi.list({ limit: 500 });
                        const availableIngredients = response.items || [];

                        const populatedIngredients = product.ingredients.map(prodIng => {
                            const fullIngredient = availableIngredients.find(availIng => availIng.id === prodIng.ingredient_id);
                            return {
                                ingredient_id: prodIng.ingredient_id,
                                quantity_per_keg: prodIng.quantity_per_keg,
                                ingredient_name: fullIngredient?.name || 'Unknown Ingredient',
                                category: fullIngredient?.category || 'Unknown',
                                unit: fullIngredient?.unit || 'Unknown',
                            };
                        });
                        setSelectedIngredients(populatedIngredients);
                    } catch (error) {
                        console.error("Error fetching ingredient details:", error);
                        showError("Could not load ingredient details.");
                    }
                };
                fetchIngredientDetails();
            } else {
                setSelectedIngredients([]);
            }
        }
    }, [product, showError]);

    // Auto-calculate total_cogs when cost fields change
    useEffect(() => {
        const { coffee_cost, ingredient_cost, can_cost, packing_cost } = formData;

        // Only calculate if all values are defined
        if (
            coffee_cost !== undefined &&
            ingredient_cost !== undefined &&
            can_cost !== undefined &&
            packing_cost !== undefined
        ) {
            const total = Number(coffee_cost) +
                Number(ingredient_cost) +
                Number(can_cost) +
                Number(packing_cost);

            setFormData(prev => ({
                ...prev,
                total_cogs: Number(total.toFixed(2))
            }));
        }
        // We're including formData in the dependency array to satisfy the ESLint rule
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formData.coffee_cost,
        formData.ingredient_cost,
        formData.can_cost,
        formData.packing_cost
    ]);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        // For numeric fields, store as number if valid
        const numericFields = [
            'coffee_cost', 'ingredient_cost',
            'can_cost', 'packing_cost', 'total_cogs',
            'wholesale_price', 'retail_price', 'total_wholesale_volume',
            'coffee_spend', 'ingredient_spend'
        ];

        const isNumericField = numericFields.includes(name);
        let processedValue = newValue;

        if (isNumericField) {
            if (value === '') {
                // Allow empty string for numeric fields
                processedValue = '';
            } else {
                // Convert to number if it's a valid number
                // Allow values like "0." during typing
                if (value === '.' || value.endsWith('.')) {
                    processedValue = value;
                } else {
                    const numberValue = parseFloat(value);
                    if (!isNaN(numberValue)) {
                        // Keep the original string value if it ends with a decimal point
                        processedValue = numberValue;
                    }
                }
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    // Handle input validation on blur
    const handleBlur = (e) => {
        const { name, value } = e.target;

        // For numeric fields, validate and format on blur
        const numericFields = [
            'coffee_cost', 'ingredient_cost',
            'can_cost', 'packing_cost', 'total_cogs',
            'wholesale_price', 'retail_price', 'total_wholesale_volume',
            'coffee_spend', 'ingredient_spend'
        ];

        if (numericFields.includes(name)) {
            // If empty, show validation error
            if (value === '') {
                setErrors(prev => ({
                    ...prev,
                    [name]: 'Value cannot be empty'
                }));
                return;
            }

            // If it's just a decimal point, convert to 0
            if (value === '.') {
                setFormData(prev => ({
                    ...prev,
                    [name]: 0
                }));
                return;
            }

            // If it ends with a decimal point, remove it
            if (value.endsWith('.')) {
                setFormData(prev => ({
                    ...prev,
                    [name]: parseFloat(value)
                }));
            }
        }
    };

    // Validate form fields
    const validateForm = useCallback(() => {
        const newErrors = {};

        // Required fields
        if (!formData.flavor?.trim()) {
            newErrors.flavor = 'Flavor name is required';
        }

        // Numeric fields must be non-negative
        const numericFields = [
            'coffee_cost', 'ingredient_cost',
            'can_cost', 'packing_cost', 'total_cogs',
            'wholesale_price', 'retail_price', 'total_wholesale_volume',
            'coffee_spend', 'ingredient_spend'
        ];

        numericFields.forEach(field => {
            const validationResult = validateNonNegative(formData[field]);
            if (validationResult !== true) {
                newErrors[field] = validationResult;
            }
        });

        // Validate ingredients
        const invalidIngredients = selectedIngredients.filter(
            (ing) => !ing.quantity_per_keg || parseFloat(ing.quantity_per_keg) <= 0
        );

        if (invalidIngredients.length > 0) {
            newErrors.ingredients = 'All ingredients must have a quantity greater than 0.';
            showError('All ingredients must have a quantity greater than 0.');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, selectedIngredients, showError]);

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            const payload = {
                ...formData,
                ingredients: selectedIngredients.map(ing => ({
                    ingredient_id: ing.ingredient_id,
                    quantity_per_keg: parseFloat(ing.quantity_per_keg)
                }))
            };
            onSave(payload);
        }
    };

    // Handle keyboard events for accessibility
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onCancel();
        } else if (e.key === 'Enter' && e.ctrlKey) {
            handleSubmit(e);
        }
    };

    return (
        <div className="product-edit-panel" onKeyDown={handleKeyDown}>
            <form onSubmit={handleSubmit}>
                <div className="edit-panel-header">
                    <h3>Edit Product</h3>
                    <div className="edit-panel-actions">
                        <button
                            type="button"
                            className="cancel-btn"
                            onClick={onCancel}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="save-btn"
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner"></span>
                                    Saving...
                                </>
                            ) : 'Save Changes'}
                        </button>
                    </div>
                </div>

                <div className="edit-panel-content">
                    <div className="form-grid">
                        {/* Basic Info */}
                        <div className="form-section">
                            <h4>Basic Information</h4>

                            <div className="form-group">
                                <label htmlFor="flavor">
                                    Flavor <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="flavor"
                                    name="flavor"
                                    value={formData.flavor || ''}
                                    onChange={handleChange}
                                    className={errors.flavor ? 'error' : ''}
                                    disabled={isSaving}
                                    required
                                />
                                {errors.flavor && <div className="error-message">{errors.flavor}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description || ''}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                    rows={2}
                                />
                            </div>

                            <div className="form-group checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={formData.is_active || false}
                                        onChange={handleChange}
                                        disabled={isSaving}
                                    />
                                    Active Product
                                </label>
                            </div>
                        </div>

                        {/* Cost Section */}
                        <div className="form-section">
                            <h4>Cost Breakdown</h4>

                            <div className="form-group">
                                <label htmlFor="coffee_cost">Coffee Cost ($)</label>
                                <input
                                    type="number"
                                    id="coffee_cost"
                                    name="coffee_cost"
                                    value={formData.coffee_cost !== undefined ? formData.coffee_cost : ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className={errors.coffee_cost ? 'error' : ''}
                                    disabled={isSaving}
                                />
                                {errors.coffee_cost && <div className="error-message">{errors.coffee_cost}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="ingredient_cost">Ingredient Cost ($)</label>
                                <input
                                    type="number"
                                    id="ingredient_cost"
                                    name="ingredient_cost"
                                    value={formData.ingredient_cost !== undefined ? formData.ingredient_cost : ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className={errors.ingredient_cost ? 'error' : ''}
                                    disabled={isSaving}
                                />
                                {errors.ingredient_cost && <div className="error-message">{errors.ingredient_cost}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="can_cost">Can Cost ($)</label>
                                <input
                                    type="number"
                                    id="can_cost"
                                    name="can_cost"
                                    value={formData.can_cost !== undefined ? formData.can_cost : ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className={errors.can_cost ? 'error' : ''}
                                    disabled={isSaving}
                                />
                                {errors.can_cost && <div className="error-message">{errors.can_cost}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="packing_cost">Packing Cost ($)</label>
                                <input
                                    type="number"
                                    id="packing_cost"
                                    name="packing_cost"
                                    value={formData.packing_cost !== undefined ? formData.packing_cost : ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className={errors.packing_cost ? 'error' : ''}
                                    disabled={isSaving}
                                />
                                {errors.packing_cost && <div className="error-message">{errors.packing_cost}</div>}
                            </div>

                            <div className="form-group total">
                                <label htmlFor="total_cogs">Total COGS ($)</label>
                                <input
                                    type="number"
                                    id="total_cogs"
                                    name="total_cogs"
                                    value={formData.total_cogs !== undefined ? formData.total_cogs : ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className={`calculated ${errors.total_cogs ? 'error' : ''}`}
                                    disabled
                                />
                                {errors.total_cogs && <div className="error-message">{errors.total_cogs}</div>}
                                <div className="help-text">Auto-calculated from costs above</div>
                            </div>
                        </div>

                        {/* Pricing Section */}
                        <div className="form-section">
                            <h4>Pricing & Revenue</h4>

                            <div className="form-group">
                                <label htmlFor="wholesale_price">Wholesale Price ($)</label>
                                <input
                                    type="number"
                                    id="wholesale_price"
                                    name="wholesale_price"
                                    value={formData.wholesale_price !== undefined ? formData.wholesale_price : ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className={errors.wholesale_price ? 'error' : ''}
                                    disabled={isSaving}
                                />
                                {errors.wholesale_price && <div className="error-message">{errors.wholesale_price}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="retail_price">Retail Price ($)</label>
                                <input
                                    type="number"
                                    id="retail_price"
                                    name="retail_price"
                                    value={formData.retail_price !== undefined ? formData.retail_price : ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className={errors.retail_price ? 'error' : ''}
                                    disabled={isSaving}
                                />
                                {errors.retail_price && <div className="error-message">{errors.retail_price}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="total_wholesale_volume">Total Wholesale Volume</label>
                                <input
                                    type="number"
                                    id="total_wholesale_volume"
                                    name="total_wholesale_volume"
                                    value={formData.total_wholesale_volume !== undefined ? formData.total_wholesale_volume : ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    step="1"
                                    min="0"
                                    placeholder="0"
                                    className={errors.total_wholesale_volume ? 'error' : ''}
                                    disabled={isSaving}
                                />
                                {errors.total_wholesale_volume && <div className="error-message">{errors.total_wholesale_volume}</div>}
                            </div>
                        </div>

                        {/* Ingredients Section */}
                        <div className="form-section full-width">
                            <h4>Ingredients & Recipe</h4>
                            <IngredientPicker
                                selectedIngredients={selectedIngredients}
                                onIngredientsChange={setSelectedIngredients}
                            />
                            {errors.ingredients && <div className="error-message">{errors.ingredients}</div>}
                        </div>

                        {/* Spend Analysis & Notes */}
                        <div className="form-section">
                            <h4>Spend Analysis</h4>

                            <div className="form-group">
                                <label htmlFor="coffee_spend">Coffee Spend ($)</label>
                                <input
                                    type="number"
                                    id="coffee_spend"
                                    name="coffee_spend"
                                    value={formData.coffee_spend !== undefined ? formData.coffee_spend : ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className={errors.coffee_spend ? 'error' : ''}
                                    disabled={isSaving}
                                />
                                {errors.coffee_spend && <div className="error-message">{errors.coffee_spend}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="ingredient_spend">Ingredient Spend ($)</label>
                                <input
                                    type="number"
                                    id="ingredient_spend"
                                    name="ingredient_spend"
                                    value={formData.ingredient_spend !== undefined ? formData.ingredient_spend : ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className={errors.ingredient_spend ? 'error' : ''}
                                    disabled={isSaving}
                                />
                                {errors.ingredient_spend && <div className="error-message">{errors.ingredient_spend}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="notes">Notes</label>
                                <textarea
                                    id="notes"
                                    name="notes"
                                    value={formData.notes || ''}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="edit-panel-footer">
                    <div className="keyboard-shortcuts">
                        <span>Press <kbd>Ctrl+Enter</kbd> to save, <kbd>Esc</kbd> to cancel</span>
                    </div>
                    <div className="edit-panel-actions">
                        <button
                            type="button"
                            className="cancel-btn"
                            onClick={onCancel}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="save-btn"
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner"></span>
                                    Saving...
                                </>
                            ) : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ProductEditPanel;
